import * as React from 'react';
import './App.scss';

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Import page components
import * as HomePages from './Modules/Home/Pages';
import * as AgentPages from './Modules/Agent/Pages';
import * as AgencyPages from './Modules/Agency/Pages';

import { HomeTemplate } from './Templates/Home';
import { DashboardTemplate } from './Templates/Dashboard';

import { AuthProvider, useAuth } from './Contexts/AuthProvider';
import { Helmet } from 'react-helmet-async';
import { SITE_DESCRIPTION, SITE_NAME } from './Config/constants';
import { LoadingTemplate } from './Templates/Loading';

import './Translation/i18n';
import { ProfileSearchProvider } from './Contexts/ProfileSearchProvider';
import { ToastProvider } from './Contexts/ToastProvider';
import { Compose } from './Contexts/Compose';
import { AgentProfileLayout } from './Layouts/AgentProfileLayout';
import { DefaultLayout } from './Layouts/DefaultLayout';
import { ProfileProvider } from './Contexts/ProfileProvider';

import { ErrorBoundary } from 'react-error-boundary';
import { ModalStackProvider } from './UI/Modals/ModalStackProvider';
import { ModalHost } from './UI/Modals/ModalHost';
import { modalRegistry } from './UI/Modals/modalDefinitions';

// Top level error handling
function FatalErrorScreen({ error, resetErrorBoundary }: any) {
  return (
    <div className="container py-5">
      <div className="alert alert-danger">
        <h4 className="alert-heading mb-2">Something went wrong</h4>
        <p className="mb-3">Please try again. If this keeps happening, contact support.</p>

        <details className="small">
          <summary>Technical details</summary>
          <pre className="mt-2 mb-0">{String(error?.stack || error)}</pre>
        </details>

        <button className="btn btn-outline-danger mt-3" onClick={resetErrorBoundary}>
          Reload this view
        </button>
      </div>
    </div>
  );
}

// Small component to reuqire user is logged into to access children
type RequireAuthProps = {
  children: React.ReactElement;
};

const RequireAuth = ({ children }: RequireAuthProps) => {
  const { isAuthenticated, isLoading } = useAuth(); // your auth state

  if (isLoading()) {
    return <LoadingTemplate />;
  }

  return isAuthenticated() ? children : <Navigate to="/home/log-in" replace />;
};

// A small component that updates the body/root class when the location changes
const PageClassUpdater = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    const slug = pathname.replace(/^\//, '').replace(/\/$/, '').replace(/\//g, '-') || 'home';
    document.documentElement.setAttribute('data-page', slug);
    return () => document.documentElement.removeAttribute('data-page');
  }, [pathname]);

  return null;
};

/**
 * Main application component that configures all routes for the SPA.
 */
const App = () => {
  return (
    /* Error boundary for top level error handling */
    <ErrorBoundary
      FallbackComponent={FatalErrorScreen}
      onError={(error, info) => {
        console.error('Fatal render error:', error, info);
      }}
    >
      <Helmet defaultTitle={SITE_NAME} titleTemplate={`%s - ${SITE_NAME}`}>
        <meta name="description" content={SITE_DESCRIPTION} />
      </Helmet>
      {/* Auth Provider: Top level context providing methods to observe the current user session */}
      <AuthProvider>
        {/* Router Provider: Top level context for page navigation via React Router */}
        <Router>
          {/* Context Composer: Handles flat implementation of contexts to avoid deep nesting of contexts */}
          <Compose components={[[ToastProvider], [ProfileSearchProvider], [ModalStackProvider]]}>
            <PageClassUpdater />

            <Routes>
              {/* Public routes under HomeTemplate */}
              <Route element={<HomeTemplate />}>
                <Route path="/" element={<HomePages.Landing />} />
                <Route path="/home" element={<HomePages.Landing />} />
                <Route path="/home/agents" element={<HomePages.Agents />} />
                <Route path="/home/agencies" element={<HomePages.Agencies />} />
                <Route path="/home/pricing" element={<HomePages.Pricing />} />
                <Route path="/home/sign-up" element={<HomePages.SignUp />} />
                <Route path="/home/log-in" element={<HomePages.LogIn />} />
                <Route path="/home/reset-password" element={<HomePages.ResetPassword />} />
              </Route>

              {/* Protected routes under DashboardTemplate */}
              <Route
                element={
                  <RequireAuth>
                    <DashboardTemplate />
                  </RequireAuth>
                }
              >
                <Route element={<DefaultLayout />}>
                  {/* Agent */}
                  <Route path="/agent" element={<AgentPages.Dashboard />} />
                  <Route path="/agent/pending" element={<AgentPages.Pending />} />
                  <Route path="/agent/analytics" element={<AgentPages.Analytics />} />
                  <Route path="/agent/leads" element={<AgentPages.Leads />} />
                  <Route path="/agent/activities" element={<AgentPages.Activities />} />
                  <Route path="/agent/documents" element={<AgentPages.Documents />} />
                  <Route path="/agent/my-book" element={<AgentPages.MyBook />} />
                  <Route path="/agent/settings" element={<AgentPages.Settings />} />
                  <Route
                    path="/agent/settings/notifications"
                    element={<AgentPages.SettingsNotifications />}
                  />
                  <Route
                    path="/agent/settings/integrations"
                    element={<AgentPages.SettingsIntegrations />}
                  />

                  {/* Agency */}
                  <Route path="/agency" element={<AgencyPages.Dashboard />} />
                  <Route path="/agency/recruits" element={<AgencyPages.Recruits />} />
                  <Route path="/agency/agents" element={<AgencyPages.Agents />} />
                  <Route path="/agency/agents/:agentId" element={<AgencyPages.Agent />} />
                  <Route
                    path="/agency/agents/:agentId/notes"
                    element={<AgencyPages.AgentNotes />}
                  />
                  <Route
                    path="/agency/agents/:agentId/documents"
                    element={<AgencyPages.AgentDocuments />}
                  />
                </Route>

                {/* Agent: Profile */}
                <Route
                  path="/agent/profile/:profileId/*"
                  element={
                    <ProfileProvider>
                      <AgentProfileLayout />
                    </ProfileProvider>
                  }
                >
                  <Route index element={<AgentPages.ProfileIndex />} />
                  <Route path="providers" element={<AgentPages.ProfileProviders />} />
                  <Route path="policies" element={<AgentPages.ProfilePolicies />} />
                </Route>
              </Route>
            </Routes>
            <ModalHost registry={modalRegistry} />
          </Compose>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
